import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card, 
  Icon, 
  SearchInput,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  useCollection,
  useDocuments, 
  useDeleteDocument,
  useReprocessDocument,
  useUpdateProcessingConfig,
  useUpdateCollection,
  useReprocessCollection,
  useReclassifyCollection,
  type Document,
  type DocumentStatus,
  type ProcessingConfig,
} from '@/hooks/use-knowledge';
import { useKnowledgeBases } from '@/hooks/use-knowledge-bases';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DocumentUpload } from '@/components/DocumentUpload';
import { 
  CollectionSettings, 
  CollectionTreeView,
  ReprocessBanner,
  ReprocessDialog,
} from '@/features/knowledge/components';
import { PermissionsPanel } from '@/features/permissions/components/PermissionsPanel';

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 20;
const POLLING_INTERVAL_MS = 5000;

// =============================================================================
// Document Status Badge
// =============================================================================

const statusStyles: Record<DocumentStatus, { 
  color: string; 
  bgColor: string;
  animate?: boolean;
}> = {
  pending: { 
    color: 'text-yellow-700 dark:text-yellow-300', 
    bgColor: 'bg-yellow-100 dark:bg-yellow-900',
  },
  processing: { 
    color: 'text-blue-700 dark:text-blue-300', 
    bgColor: 'bg-blue-100 dark:bg-blue-900',
    animate: true 
  },
  ready: { 
    color: 'text-green-700 dark:text-green-300', 
    bgColor: 'bg-green-100 dark:bg-green-900',
  },
  failed: { 
    color: 'text-red-700 dark:text-red-300', 
    bgColor: 'bg-red-100 dark:bg-red-900',
  },
};

function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  const { t } = useI18n();
  const config = statusStyles[status] || statusStyles.pending;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${config.bgColor} ${config.color}`}>
      {config.animate && <Icon name="loader" size={12} className="animate-spin" />}
      {t(`admin.knowledge.status.${status}`)}
    </span>
  );
}

// =============================================================================
// Document Type Icon
// =============================================================================

function getDocumentIcon(mimeType: string | null): string {
  if (!mimeType) return 'file';
  if (mimeType.includes('pdf')) return 'file-text';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'file-text';
  if (mimeType.includes('text') || mimeType.includes('markdown')) return 'file-text';
  return 'file';
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string, locale: string = 'en-US'): string {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function DocumentsSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-4 w-1/4" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Collection Documents Page
// =============================================================================

export function CollectionDocumentsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { id: collectionId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'documents';
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [detailTarget, setDetailTarget] = useState<Document | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');

  // Fetch collection info
  const { 
    data: collection, 
    isLoading: isLoadingCollection,
    isError: isCollectionError,
    refetch: refetchCollection,
  } = useCollection(collectionId || '');

  // Fetch documents
  const { 
    data: documentsData, 
    isLoading: isLoadingDocuments, 
    isError: isDocumentsError,
    refetch,
  } = useDocuments(collectionId || '', { page, page_size: PAGE_SIZE });

  // Mutations
  const deleteDocument = useDeleteDocument(collectionId || '');
  const reprocessDocument = useReprocessDocument();
  const updateProcessingConfig = useUpdateProcessingConfig();
  const updateCollection = useUpdateCollection();
  const reprocessCollection = useReprocessCollection();
  const reclassifyCollection = useReclassifyCollection();
  const collectionMode = collection?.mode || 'rag';

  // Knowledge Bases for assignment dropdown
  const { data: knowledgeBasesData } = useKnowledgeBases({ page: 1, page_size: 100 });

  // Polling for processing documents or reprocessing collections
  useEffect(() => {
    const hasPendingOrProcessing = documentsData?.data.some(
      (doc) => doc.status === 'pending' || doc.status === 'processing'
    );
    const isReprocessing = collection?.reprocessing_status === 'in_progress';

    if (hasPendingOrProcessing || isReprocessing) {
      const interval = setInterval(() => {
        refetch();
        if (isReprocessing) refetchCollection();
      }, POLLING_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [documentsData, collection?.reprocessing_status, refetch, refetchCollection]);

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  const handleKbAssignmentChange = async (value: string) => {
    if (!collection) return;
    try {
      await updateCollection.mutateAsync({
        id: collection.id,
        knowledge_base_id: value === '__none__' ? '' : value,
      });
      refetchCollection();
      toast.success(t('admin.knowledge.documents.kbAssigned'));
    } catch {
      toast.error(t('admin.knowledge.documents.kbAssignError'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await deleteDocument.mutateAsync(deleteTarget.id);
      toast.success(t('admin.knowledge.documents.deleteSuccess', { name: deleteTarget.filename }));
      setDeleteTarget(null);
    } catch {
      toast.error(t('admin.knowledge.documents.deleteFailed'));
    }
  };

  const handleReprocess = async (document: Document) => {
    try {
      await reprocessDocument.mutateAsync(document.id);
      toast.success(t('admin.knowledge.documents.reprocessSuccess', { name: document.filename }));
      refetch();
    } catch {
      toast.error(t('admin.knowledge.documents.reprocessFailed'));
    }
  };

  const handleUploadComplete = () => {
    setShowUpload(false);
    refetch();
    toast.success(t('admin.knowledge.documents.uploadSuccess'));
  };

  // Settings tab handlers
  const handleSaveConfig = async (config: Partial<ProcessingConfig>) => {
    if (!collectionId) return;
    try {
      await updateProcessingConfig.mutateAsync({
        id: collectionId,
        config: {
          chunking: config.chunking,
          embedding: config.embedding,
          enrichment: config.enrichment,
        },
      });
      toast.success(t('admin.knowledge.settings.saveSuccess'));
    } catch {
      toast.error(t('admin.knowledge.settings.saveError'));
    }
  };

  const handleReprocessAll = async () => {
    if (!collectionId) return;
    try {
      await reprocessCollection.mutateAsync(collectionId);
      toast.success(t('admin.knowledge.settings.reprocessSuccess'));
      setShowReprocessDialog(false);
      refetchCollection();
    } catch {
      toast.error(t('admin.knowledge.settings.reprocessError'));
    }
  };

  const handleReclassifyAll = async () => {
    if (!collectionId) return;
    try {
      await reclassifyCollection.mutateAsync(collectionId);
      toast.success(t('admin.knowledge.settings.reclassifySuccess'));
    } catch {
      toast.error(t('admin.knowledge.settings.reclassifyError'));
    }
  };

  // Error states
  if (isCollectionError || (collectionId && !collection && !isLoadingCollection)) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.knowledge.documents.collectionNotFound')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('admin.knowledge.documents.collectionNotFoundHint')}
          </p>
          <Button variant="outline" onClick={() => navigate('/knowledge/collections')}>
            {t('admin.knowledge.documents.backToCollections')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/knowledge/collections')}
          >
            <Icon name="arrow-left" size={20} />
          </Button>
          <div>
            {isLoadingCollection ? (
              <>
                <Skeleton className="h-8 w-48 mb-1" />
                <Skeleton className="h-5 w-64" />
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {collection?.name || t('admin.knowledge.documents.title')}
                  </h1>
                  {collection && collectionMode === 'full_document' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      {t('admin.knowledge.collections.modeFullDocument')}
                    </span>
                  )}
                  {collection && collectionMode === 'rag' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {t('admin.knowledge.collections.modeRag')}
                    </span>
                  )}
                </div>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {collection?.description || t('admin.knowledge.documents.manageDocuments')}
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={() => navigate(`/knowledge/collections/${collectionId}/edit`)}
          >
            <Icon name="edit" size={16} className="mr-2" />
            {t('common.edit')}
          </Button>
          {activeTab === 'documents' && (
            <Button leftIcon="upload" onClick={() => setShowUpload(true)}>
              {t('admin.knowledge.documents.upload')}
            </Button>
          )}
        </div>
      </div>

      {/* Reprocess Banner */}
      {collection && (
        <ReprocessBanner status={collection.reprocessing_status} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="documents">
            <Icon name="file-text" size={16} className="mr-2" />
            {t('admin.knowledge.documents.title')}
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Icon name="settings" size={16} className="mr-2" />
            {t('admin.knowledge.documents.settings')}
          </TabsTrigger>
          <TabsTrigger value="access">
            <Icon name="shield" size={16} className="mr-2" />
            {t('admin.knowledge.documents.access') || 'Access'}
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents">
          {/* Stats */}
          {collection && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.knowledge.documents.totalDocuments')}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {collection.document_count}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {collectionMode === 'full_document'
                    ? t('admin.knowledge.documents.totalMarkdownVersions')
                    : t('admin.knowledge.documents.totalChunks')}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(collectionMode === 'full_document'
                    ? collection.document_count
                    : collection.total_chunks).toLocaleString()}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.knowledge.form.mode')}</div>
                <div className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {collectionMode === 'full_document'
                    ? t('admin.knowledge.collections.modeFullDocument')
                    : t('admin.knowledge.collections.modeRag')}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.knowledge.documents.chunkSize')}</div>
                <div className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {collectionMode === 'full_document'
                    ? '-'
                    : t('admin.knowledge.documents.chunkSizeTokens', { size: collection.processing_config?.chunking?.max_tokens ?? collection.chunk_size })}
                </div>
              </Card>
            </div>
          )}

          {/* Search Filter + View Toggle */}
          <div className="mb-4 flex items-center gap-2">
            <SearchInput
              placeholder={t('admin.knowledge.documents.searchPlaceholder')}
              value={documentSearch}
              onChange={(e) => setDocumentSearch(e.target.value)}
              onClear={() => setDocumentSearch('')}
              className="max-w-sm"
            />
            <div className="ml-auto flex items-center gap-1 border rounded-lg p-0.5 dark:border-gray-700">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('list')}
                title={t('admin.knowledge.documents.viewList')}
              >
                <Icon name="file-text" size={16} />
              </Button>
              <Button
                variant={viewMode === 'tree' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('tree')}
                title={t('admin.knowledge.documents.viewTree')}
              >
                <Icon name="folder-open" size={16} />
              </Button>
            </div>
          </div>

          {/* Tree View */}
          {viewMode === 'tree' && collectionId && (
            <CollectionTreeView
              collectionId={collectionId}
              onDocumentClick={(doc) => setDetailTarget(doc)}
              renderDocumentRow={(doc) => (
                <Card
                  className="p-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => setDetailTarget(doc)}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                      <Icon name={getDocumentIcon(doc.content_type) as 'file' | 'file-text'} size={24} className="text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{doc.filename}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                        <span>{formatFileSize(doc.file_size_bytes)}</span>
                        <span>•</span>
                        <span>{formatDate(doc.created_at, locale === 'es' ? 'es-ES' : 'en-US')}</span>
                      </div>
                    </div>
                    <DocumentStatusBadge status={doc.status} />
                  </div>
                </Card>
              )}
            />
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <>

          {/* Loading State */}
          {isLoadingDocuments && <DocumentsSkeleton />}

          {/* Error State */}
          {isDocumentsError && (
            <Card className="p-8 text-center">
              <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('admin.knowledge.documents.loadFailed')}
              </h3>
              <Button variant="outline" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Card>
          )}

          {/* Empty State */}
          {!isLoadingDocuments && !isDocumentsError && documentsData?.data.length === 0 && (
            <Card className="p-8 text-center">
              <Icon name="file-text" size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('admin.knowledge.documents.noDocuments')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('admin.knowledge.documents.uploadHint')}
              </p>
              <Button leftIcon="upload" onClick={() => setShowUpload(true)}>
                {t('admin.knowledge.documents.upload')}
              </Button>
            </Card>
          )}

          {/* Documents List */}
          {!isLoadingDocuments && !isDocumentsError && documentsData && documentsData.data.length > 0 && (
            <>
              <div className="space-y-2">
                {documentsData.data
                  .filter((doc) => !documentSearch || doc.filename.toLowerCase().includes(documentSearch.toLowerCase()))
                  .map((doc) => (
                  <Card 
                    key={doc.id} 
                    className="p-4 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => setDetailTarget(doc)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                        <Icon 
                          name={getDocumentIcon(doc.content_type) as 'file' | 'file-text'} 
                          size={24} 
                          className="text-gray-600 dark:text-gray-400" 
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {doc.filename}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                          <span>{formatFileSize(doc.file_size_bytes)}</span>
                          <span>•</span>
                          <span>{formatDate(doc.created_at, locale === 'es' ? 'es-ES' : 'en-US')}</span>
                          {collectionMode === 'full_document' && doc.status === 'ready' && (
                            <>
                              <span>•</span>
                              <span>1 {t('admin.knowledge.documents.markdownVersion')}</span>
                            </>
                          )}
                          {collectionMode !== 'full_document' && doc.chunk_count > 0 && (
                            <>
                              <span>•</span>
                              <span>{doc.chunk_count} {t('common.chunks')}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <DocumentStatusBadge status={doc.status} />

                      {/* Actions */}
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Icon name="more-vertical" size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDetailTarget(doc)}>
                              <Icon name="eye" size={14} className="mr-2" />
                              {t('common.viewDetails')}
                            </DropdownMenuItem>
                            {doc.status === 'failed' && (
                              <DropdownMenuItem onClick={() => handleReprocess(doc)}>
                                <Icon name="refresh-cw" size={14} className="mr-2" />
                                {t('admin.knowledge.documents.retryProcessing')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setDeleteTarget(doc)}
                            >
                              <Icon name="trash-2" size={14} className="mr-2" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {documentsData.meta.total > documentsData.meta.page_size && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {t('common.previous')}
                  </Button>
                  <span className="text-sm text-gray-500 dark:text-gray-400 px-4">
                    {t('common.pageOf', { page: documentsData.meta.page, totalPages: documentsData.meta.total_pages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= documentsData.meta.total_pages}
                    onClick={() => setPage(page + 1)}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              )}
            </>
          )}
          </>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          {isLoadingCollection && (
            <Card className="p-8">
              <Skeleton className="h-8 w-48 mb-4" />
              <Skeleton className="h-64 w-full" />
            </Card>
          )}
          {collection && (
            <div className="space-y-6">
              {/* KB Assignment */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-1">{t('admin.knowledge.documents.kbAssignment')}</h3>
                <p className="text-sm text-muted-foreground mb-4">{t('admin.knowledge.documents.kbAssignmentHint')}</p>
                <Select
                  value={collection.knowledge_base_id || '__none__'}
                  onValueChange={handleKbAssignmentChange}
                  disabled={updateCollection.isPending}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder={t('admin.knowledge.documents.kbNone')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('admin.knowledge.documents.kbNone')}</SelectItem>
                    {knowledgeBasesData?.data?.map((kb) => (
                      <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Card>

              <CollectionSettings
                collectionId={collection.id}
                mode={collection.mode}
                config={collection.processing_config}
                activeEmbeddingModel={collection.embedding_model}
                embeddingLocked={collection.embedding_locked}
                documentCount={collection.document_count}
                onSave={handleSaveConfig}
                onReprocess={() => {
                  setShowReprocessDialog(true);
                  return Promise.resolve();
                }}
                onReclassify={handleReclassifyAll}
              />
            </div>
          )}
        </TabsContent>

        {/* Access Tab */}
        <TabsContent value="access">
          {collectionId ? (
            <Card className="p-6">
              <PermissionsPanel entityType="collection" entityId={collectionId} />
            </Card>
          ) : (
            <Card className="p-8">
              <Skeleton className="h-8 w-48 mb-4" />
              <Skeleton className="h-48 w-full" />
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('admin.knowledge.documents.uploadDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.knowledge.documents.uploadDialogDescription', { name: collection?.name || '' })}
            </DialogDescription>
          </DialogHeader>
          {collectionId && (
            <DocumentUpload 
              collectionId={collectionId} 
              onUploadComplete={handleUploadComplete}
              onCancel={() => setShowUpload(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Document Details Dialog */}
      <Dialog open={!!detailTarget} onOpenChange={(open) => !open && setDetailTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.knowledge.documents.detailsTitle')}</DialogTitle>
          </DialogHeader>
          {detailTarget && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('admin.knowledge.documents.details.filename')}
                </label>
                <p className="text-gray-900 dark:text-gray-100">{detailTarget.filename}</p>
              </div>
              {/* ISS-026: Show normalized path. Prefer source_path (external) → relative_path (local). */}
              {(detailTarget.source_path || detailTarget.relative_path) && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {detailTarget.source_path
                      ? t('admin.knowledge.documents.details.sourcePath')
                      : t('admin.knowledge.documents.details.path')}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 text-sm font-mono break-all">
                    {detailTarget.source_path || detailTarget.relative_path}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.knowledge.documents.details.status')}
                  </label>
                  <div className="mt-1">
                    <DocumentStatusBadge status={detailTarget.status} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.knowledge.documents.details.size')}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {formatFileSize(detailTarget.file_size_bytes)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {collectionMode === 'full_document'
                      ? t('admin.knowledge.documents.details.markdownVersion')
                      : t('admin.knowledge.documents.details.chunks')}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {collectionMode === 'full_document'
                      ? detailTarget.status === 'ready' ? 1 : 0
                      : detailTarget.chunk_count}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.knowledge.documents.details.mimeType')}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 truncate">
                    {detailTarget.content_type || t('admin.knowledge.documents.details.unknown')}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('admin.knowledge.documents.details.uploaded')}
                </label>
                <p className="text-gray-900 dark:text-gray-100">
                  {formatDate(detailTarget.created_at, locale === 'es' ? 'es-ES' : 'en-US')}
                </p>
              </div>
              {detailTarget.error_message && (
                <div>
                  <label className="text-sm font-medium text-red-500">
                    {t('admin.knowledge.documents.details.error')}
                  </label>
                  <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">
                    {detailTarget.error_message}
                  </p>
                </div>
              )}
              {detailTarget.tags && detailTarget.tags.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.knowledge.documents.details.tags')}
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {detailTarget.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.knowledge.documents.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.knowledge.documents.deleteDescription', { name: deleteTarget?.filename ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteDocument.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reprocess Confirmation Dialog */}
      {collection && (
        <ReprocessDialog
          open={showReprocessDialog}
          onOpenChange={setShowReprocessDialog}
          collectionName={collection.name}
          documentCount={collection.document_count}
          onConfirm={handleReprocessAll}
        />
      )}
    </div>
  );
}
